import React from 'react'
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Divider
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import UsageChart from '../UsageChart'

interface EngineUsage {
  engineName: string
  requests: number
  minutes: number
  cost: number
  percentage: number
}

interface UsageEnginesTabProps {
  engineUsage: EngineUsage[]
  formatTime: (minutes: number) => string
}

const UsageEnginesTab: React.FC<UsageEnginesTabProps> = ({
  engineUsage,
  formatTime
}) => {
  const { t } = useTranslation()

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        {engineUsage.length > 0 && (
          <UsageChart
            dailyUsage={[]}
            engineUsage={engineUsage}
            type="engines"
            title={t('usageByEngine', 'Usage by Engine')}
            height={400}
          />
        )}
      </Grid>
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ fontWeight: 700, color: '#1a237e' }}
            >
              {t('engineBreakdown', 'Engine Breakdown')}
            </Typography>
            {engineUsage.map((engine, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {engine.engineName}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ fontWeight: 600 }}
                  >
                    {(engine.percentage || 0).toFixed(1)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={engine.percentage || 0}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    mb: 1,
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4
                    }
                  }}
                />
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="textSecondary">
                      {t('requests', 'Requests')}: {engine.requests.toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="textSecondary">
                      {t('minutes', 'Minutes')}: {formatTime(engine.minutes)}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="textSecondary">
                      {t('cost', 'Cost')}: €{(engine.cost || 0).toFixed(2)}
                    </Typography>
                  </Grid>
                </Grid>
                {index < engineUsage.length - 1 && <Divider sx={{ mt: 2 }} />}
              </Box>
            ))}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  )
}

export default UsageEnginesTab
